import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { FaireMarketIcon } from "@faire/retailer-visitor-shared/features/FaireMarkets/FaireMarketIcon";
import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Opacity } from "@faire/web--source/slate/Color";
import { Input } from "@faire/web--source/slate/Input";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { eventsThemeColors } from "@faire/web--source/slate/Theme/eventsThemeColors";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackBrandLeadsApplyToSellOnFaireClick } from "@faire/web-api--source/events/brandLeads/click/applyToSellOnFaire";
import { trackBrandLeadsBrandsPageSignInClick } from "@faire/web-api--source/events/brandLeads/click/brandsPageSignIn";
import { trackBrandLeadsScheduleACallClick } from "@faire/web-api--source/events/brandLeads/click/scheduleACall";
import brands from "@faire/web-api--source/routes/brands";
import { observer } from "mobx-react";
import React from "react";
import styled, { css, keyframes } from "styled-components";
import { CTAPosition, StyledSellOnFaireButton } from "./const";
import { useBrandsLandingPageState } from "./hooks/useBrandsLandingPageState";
var ANIMATION_LENGTH = 300;
var NAV_HEIGHT_DESKTOP = 82;
var BrandsLandingPageNavBase = function() {
    var _useMarketStore = useMarketStore([
        "showBrandLandingPage"
    ]), displayFaireMarket = _useMarketStore.showBrandLandingPage;
    var _useBrandsLandingPageState = useBrandsLandingPageState(), showBanner = _useBrandsLandingPageState.showBanner, isPersonalized = _useBrandsLandingPageState.isPersonalized, calendlyLink = _useBrandsLandingPageState.calendlyLink, emailAddress = _useBrandsLandingPageState.emailAddress, handleEmailChange = _useBrandsLandingPageState.handleEmailChange, goToForm = _useBrandsLandingPageState.goToForm, isMarketSectionVisible = _useBrandsLandingPageState.isMarketSectionVisible;
    if (!showBanner) {
        return null;
    }
    var Prompt = isPersonalized ? /*#__PURE__*/ _jsx(PersonalizedPrompt, {
        calendlyLink: calendlyLink,
        displayFaireMarket: displayFaireMarket
    }) : /*#__PURE__*/ _jsx(UnpersonalizedPrompt, {
        emailAddress: emailAddress,
        displayFaireMarket: displayFaireMarket,
        handleEmailChange: handleEmailChange,
        goToForm: goToForm
    });
    return /*#__PURE__*/ _jsx(BrandsLandingPageSignUpNav, {
        $isFaireMarket: displayFaireMarket,
        children: /*#__PURE__*/ _jsx(ResponsiveContentV2, {
            tabletAndBelow: /*#__PURE__*/ _jsxs(Column, {
                children: [
                    displayFaireMarket && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(MobileOrTabletBody, {
                                isMarketSectionVisible: isMarketSectionVisible
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: "1x"
                            })
                        ]
                    }),
                    Prompt
                ]
            }),
            desktopAndAbove: /*#__PURE__*/ _jsxs(FullWidthRow, {
                align: "center",
                justify: "space-between",
                children: [
                    /*#__PURE__*/ _jsx(DesktopBody, {
                        displayFaireMarket: displayFaireMarket,
                        isMarketSectionVisible: isMarketSectionVisible
                    }),
                    Prompt
                ]
            })
        })
    });
};
export var BrandsLandingPageNav = observer(BrandsLandingPageNavBase);
var PersonalizedPromptBase = function(param) {
    var calendlyLink = param.calendlyLink, displayFaireMarket = param.displayFaireMarket;
    return /*#__PURE__*/ _jsxs(Row, {
        align: "center",
        justify: "center",
        children: [
            /*#__PURE__*/ _jsx(StyledSellOnFaireButton, {
                style: {
                    width: "max-content",
                    minWidth: "200px"
                },
                linkTo: calendlyLink,
                target: "_blank",
                onClick: function() {
                    trackBrandLeadsScheduleACallClick(CTAPosition.STICKY_BANNER);
                },
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "5iQ4eN",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Schedule a Call"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "4x"
            }),
            /*#__PURE__*/ _jsx(Button, {
                tertiaryColor: displayFaireMarket ? "faireBlack" : "white",
                onClick: function() {
                    return trackBrandLeadsBrandsPageSignInClick(CTAPosition.STICKY_BANNER);
                },
                linkTo: brands({
                    signIn: "1"
                }),
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "Ub+AGc",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Sign In"
                        }
                    ]
                })
            })
        ]
    });
};
var PersonalizedPrompt = observer(PersonalizedPromptBase);
var UnpersonalizedPromptBase = function(param) {
    var emailAddress = param.emailAddress, displayFaireMarket = param.displayFaireMarket, handleEmailChange = param.handleEmailChange, goToForm = param.goToForm;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isEurope = useRetailerStore([
        "isEurope"
    ]).isEurope;
    var handleGoToFormClickHandler = function() {
        trackBrandLeadsApplyToSellOnFaireClick(CTAPosition.STICKY_BANNER);
        goToForm();
    };
    return /*#__PURE__*/ _jsxs(Row, {
        justify: "center",
        children: [
            /*#__PURE__*/ _jsx(NavEmailInputFieldWrapper, {
                children: /*#__PURE__*/ _jsx(NavEmailInputField, {
                    placeholder: strictLocalize({
                        id: "bc+vrX",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Email address"
                            }
                        ]
                    }),
                    value: emailAddress,
                    autoComplete: "email",
                    onInput: handleEmailChange,
                    $isFaireMarket: displayFaireMarket
                })
            }),
            /*#__PURE__*/ _jsx(NavApplyButton, {
                onClick: handleGoToFormClickHandler,
                variant: displayFaireMarket ? "primary" : "secondary",
                $isFaireMarket: displayFaireMarket,
                children: isEurope ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "9Uhu4R",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Get Started"
                        }
                    ]
                }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "9KKJYM",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Start Selling"
                        }
                    ]
                })
            })
        ]
    });
};
var UnpersonalizedPrompt = observer(UnpersonalizedPromptBase);
var DesktopBodyBase = function(param) {
    var displayFaireMarket = param.displayFaireMarket, isMarketSectionVisible = param.isMarketSectionVisible;
    var isEurope = useRetailerStore([
        "isEurope"
    ]).isEurope;
    var marketName = useMarketStore([
        "marketName"
    ]).marketName;
    if (displayFaireMarket) {
        return /*#__PURE__*/ _jsxs(Row, {
            align: "center",
            children: [
                /*#__PURE__*/ _jsx(StyledIcon, {
                    $isMarketSectionVisible: isMarketSectionVisible
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    width: "2x"
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansMedium",
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "l8178n",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Join Faire in time to sell at "
                            },
                            {
                                type: 1,
                                value: "marketEventName"
                            }
                        ],
                        values: {
                            marketEventName: marketName
                        }
                    })
                })
            ]
        });
    } else if (isEurope) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(MaxWidthTypography, {
                    variant: "subheadingSansMedium",
                    color: Core.text.inverse,
                    children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                        id: "bslC/A",
                        defaultMessage: [
                            {
                                type: 0,
                                value: "Sell to hundreds of thousands of retailers across Europe and the U.S."
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    width: "10x"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(MaxWidthTypography, {
                variant: "subheadingSansMedium",
                color: Core.text.inverse,
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "V+jpY5",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Access hundreds of thousands of retailers around the world"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "10x"
            })
        ]
    });
};
var DesktopBody = observer(DesktopBodyBase);
var MobileOrTabletBodyBase = function(param) {
    var isMarketSectionVisible = param.isMarketSectionVisible;
    var marketName = useMarketStore([
        "marketName"
    ]).marketName;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                align: "center",
                children: [
                    /*#__PURE__*/ _jsx(StyledIcon, {
                        $isMarketSectionVisible: isMarketSectionVisible
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        width: "2x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraphSansMedium",
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "l8178n",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Join Faire in time to sell at "
                                },
                                {
                                    type: 1,
                                    value: "marketEventName"
                                }
                            ],
                            values: {
                                marketEventName: marketName
                            }
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            })
        ]
    });
};
var MobileOrTabletBody = observer(MobileOrTabletBodyBase);
var MaxWidthTypography = styled(Typography).withConfig({
    componentId: "sc-dc2fcd1a-0"
})([
    "max-width:600px;"
]);
var slideIn = keyframes([
    "0%{top:-",
    "px;}100%{top:0;}"
], NAV_HEIGHT_DESKTOP);
var BrandsLandingPageSignUpNav = styled.nav.withConfig({
    componentId: "sc-dc2fcd1a-1"
})([
    "position:fixed;height:fit-content;width:100vw;background-color:",
    ";display:flex;flex-direction:row;align-items:center;justify-content:center;box-shadow:0 1px 2px rgba(0,0,0,0.1);z-index:12;min-height:",
    "px;top:0;animation:",
    " ",
    "ms ease-in-out 0s;padding:",
    ";",
    "{padding:",
    ";}",
    "{padding:",
    ";}"
], function(param) {
    var $isFaireMarket = param.$isFaireMarket;
    return $isFaireMarket ? eventsThemeColors.faireMarket.lightBackground : Decorative.shade.yellow;
}, NAV_HEIGHT_DESKTOP, slideIn, ANIMATION_LENGTH, getSpacing(0, "5x"), responsiveMediaQueries.mobile, getSpacing("2x"), responsiveMediaQueries.tablet, getSpacing("2x", "3x"));
var NavEmailInputFieldWrapper = styled(Row).withConfig({
    componentId: "sc-dc2fcd1a-2"
})([
    "width:280px;height:50px;",
    "{width:200px;}"
], responsiveMediaQueries.tabletAndBelow);
var NavEmailInputField = styled(Input).withConfig({
    componentId: "sc-dc2fcd1a-3"
})([
    "width:280px !important;height:50px !important;border:1.2px solid ",
    " !important;background:none !important;",
    "{width:200px !important;}",
    "{height:40px !important;margin-top:5px !important;}input{",
    "}&:focus,&:focus-within{",
    "}"
], function(param) {
    var $isFaireMarket = param.$isFaireMarket;
    return $isFaireMarket ? Core.border.default : Core.border.muted;
}, responsiveMediaQueries.tabletAndBelow, responsiveMediaQueries.mobile, function(param) {
    var $isFaireMarket = param.$isFaireMarket;
    return $isFaireMarket ? css([
        "color:",
        ";&::placeholder{color:",
        ";}"
    ], Core.text.default, Core.text.default) : css([
        "color:",
        ";&::placeholder{color:",
        ";}"
    ], Core.text.inverse, Core.text.inverse);
}, function(param) {
    var $isFaireMarket = param.$isFaireMarket;
    return $isFaireMarket ? css([
        "border-color:",
        ";input{&::placeholder{color:",
        ";}}"
    ], Core.text.default, Core.text.default + Opacity.O_50) : css([
        "border-color:",
        ";input{&::placeholder{color:",
        ";}}"
    ], Core.border.muted, Core.text.inverse + Opacity.O_50);
});
var NavApplyButton = styled(Button).withConfig({
    componentId: "sc-dc2fcd1a-4"
})([
    "min-width:130px;height:50px;width:max-content;",
    " ",
    "{min-width:110px;margin-top:5px;width:100%;height:40px;}"
], function(param) {
    var $isFaireMarket = param.$isFaireMarket;
    return $isFaireMarket ? css([
        "color:",
        ";background-color:",
        ";border:1.2px solid ",
        ";"
    ], Core.text.inverse, Core.surface.default, Core.border.default) : css([
        "color:",
        ";background-color:",
        ";border:1.2px solid ",
        ";"
    ], Core.text.default, Core.surface.inverse, Core.border.muted);
}, responsiveMediaQueries.mobile);
var FullWidthRow = styled(Row).withConfig({
    componentId: "sc-dc2fcd1a-5"
})([
    "width:100%;"
]);
var StyledIcon = styled(FaireMarketIcon).attrs({
    size: "32px"
}).withConfig({
    componentId: "sc-dc2fcd1a-6"
})([
    "",
    " ",
    "{align-self:start;}"
], function(param) {
    var $isMarketSectionVisible = param.$isMarketSectionVisible;
    return $isMarketSectionVisible ? css([
        "animation:rotation 4s infinite linear;@keyframes rotation{from{transform:rotate(0deg);}to{transform:rotate(359deg);}}"
    ]) : null;
}, responsiveMediaQueries.mobile);
